import { Receipt } from '@mui/icons-material';
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTableColumn, IFilterChip, PaginatedProps, StandardListViewV2 } from '../../Components/CoreLib/library';
import { triaxReferenceNumberColumnKeySuffixes } from '../../constants/triaxReferenceNumberColumnKeySuffixes';
import { OrderDto } from '../../dtos';
import { useGetOrderQuery } from '../../store/generated/generatedApi';
import { emptyGuid, formatDate, useListViewQueryParams, usePageTitleSetter } from '../../util';
import { OrderFilterDialog } from './Components/OrderFilterDialog';
import { useOrderListFilters } from './Utils/useOrderListFilters';

const ORDER_FILTER_CHIP_KEYS = {
    customer: 'Customer',
    material: 'Material',
    orderDate: 'Order date',
} as const;

const defaultPaginationProps: PaginatedProps = {
    sorting: [{ key: 'ORDER_DATE', direction: 'desc' }, { key: 'ORDER_NUMBER', direction: 'desc' }],
    page: 0,
    pageSize: 25,
};

export const GlobalOrderListView: FC = () => {
    const navigate = useNavigate();
    usePageTitleSetter('Orders');

    const { orderFilters, setOrderFilters } = useOrderListFilters({ includeCustomer: true });
    const [isFilterDialogVisible, setIsFilterDialogVisible] = useState(false);

    const { paginationProps, searchText, includeInactive, onPaginationChange, onSearchChange, onShowInactiveChange } = useListViewQueryParams({
        defaultPaginationProps,
    });

    const additionalQueryParameters = useMemo(() => {
        const params: Record<string, string> = {};
        if (orderFilters.customerId !== emptyGuid) {
            params.customerId = orderFilters.customerId;
        }
        if (orderFilters.materialId !== emptyGuid) {
            params.materialId = orderFilters.materialId;
        }
        if (orderFilters.orderDate) {
            params.orderDate = orderFilters.orderDate.toISOString();
        }
        return params;
    }, [orderFilters]);

    const filterChips = useMemo<IFilterChip[]>(() => {
        const chips: IFilterChip[] = [];
        if (orderFilters.customerId !== emptyGuid) {
            const customerChipText = orderFilters.customerLabel?.trim() || 'Selected customer';
            chips.push({ key: ORDER_FILTER_CHIP_KEYS.customer, text: customerChipText });
        }
        if (orderFilters.materialId !== emptyGuid && orderFilters.materialLabel) {
            chips.push({ key: ORDER_FILTER_CHIP_KEYS.material, text: orderFilters.materialLabel });
        }
        if (orderFilters.orderDate) {
            chips.push({ key: ORDER_FILTER_CHIP_KEYS.orderDate, text: formatDate(orderFilters.orderDate) });
        }
        return chips;
    }, [orderFilters]);

    const handleFilterChipDelete = useCallback((filterChipKey: string) => {
        setOrderFilters((prev) => {
            switch (filterChipKey) {
                case ORDER_FILTER_CHIP_KEYS.customer:
                    return { ...prev, customerId: emptyGuid, customerLabel: '' };
                case ORDER_FILTER_CHIP_KEYS.material:
                    return { ...prev, materialId: emptyGuid, materialLabel: '' };
                case ORDER_FILTER_CHIP_KEYS.orderDate:
                    return { ...prev, orderDate: undefined };
                default:
                    return prev;
            }
        });
    }, [setOrderFilters]);

    const tableColumns: DataTableColumn<OrderDto>[] = useMemo(
        () => [
            { key: 'customerName', label: 'Customer', sortKey: 'CUSTOMER' },
            { key: 'customerNumber', label: 'Customer #', sortKey: 'CUSTOMER_NUMBER' },
            { key: 'projectName', label: 'Project Name', sortKey: 'PROJECT_NAME' },
            { key: 'jobName', label: 'Job Name', sortKey: 'JOB_NAME' },
            { key: 'addressLine1', label: 'Address', sortKey: 'ADDRESS' },
            { key: 'city', label: 'City', sortKey: 'CITY' },
            { key: 'jobNumber', label: 'Job #', sortKey: 'JOB_NUMBER' },
            { key: 'poNumber', label: 'PO #', sortKey: 'PO_NUMBER' },
            { key: 'orderNumber', label: 'Order #', sortKey: 'ORDER_NUMBER' },
            {
                key: 'orderDate',
                label: 'Order Date',
                sortKey: 'ORDER_DATE',
                fieldMapper: (row) => (row.orderDate ? formatDate(row.orderDate) : ''),
            },
            { key: 'orderer', label: 'Orderer', sortKey: 'ORDERER', fieldMapper: (row) => row.orderer || '' },
            { key: 'status', label: 'Status', sortKey: 'STATUS', unsorted: true },
        ],
        []
    );

    const handleTableEdit = (id: string, record: OrderDto) => {
        navigate(`/job/${record.jobId}/order/${id}`);
    };

    return (
        <>
            <OrderFilterDialog
                isVisible={isFilterDialogVisible}
                onClose={() => setIsFilterDialogVisible(false)}
                filterProps={orderFilters}
                setFilterProps={setOrderFilters}
                showCustomerFilter
            />
            <StandardListViewV2
                referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                enableMultiSort
                headerIcon={<Receipt />}
                headerTitle='Orders'
                breadCrumbs={[{ label: 'Home', navLink: '/' }]}
                defaultPaginationProps={defaultPaginationProps}
                initialPaginatedProps={paginationProps}
                initialSearchText={searchText}
                initialShowInactive={includeInactive}
                getDataQuery={useGetOrderQuery}
                onPaginationChange={onPaginationChange}
                onSearchChange={onSearchChange}
                onShowInactiveChange={onShowInactiveChange}
                isShowActiveToggleVisible
                tableColumns={tableColumns}
                entityNameSingular='Order'
                permissionName='order'
                handleEdit={handleTableEdit}
                paginationOnTop={true}
                stickyTableHeader={true}
                handleFilterClicked={() => setIsFilterDialogVisible(true)}
                filterChips={filterChips}
                handleFilterChipDelete={handleFilterChipDelete}
                additionalQueryParameters={additionalQueryParameters}
            />
        </>
    );
};
